import { computed, DestroyRef, inject, Injectable, signal } from '@angular/core';
import { ApizUIDataField } from '@compiere-ws/models/apiz-domains';
import { IupicsData } from '@iupics-manager/models/iupics-data';
import { ToolbarProcess } from '@iupics-manager/models/toolbar-item';
import { DataUtils } from '@iupics-util/tools/data.utils';

@Injectable()
export class EditViewService {
  uuid = DataUtils.uuid();
  #destroyRef = inject(DestroyRef);

  #processes = signal<{ [key: string]: ToolbarProcess }>({});
  processes = computed(() => {
    return Object.values(this.#processes()).filter((process) => process.isDisplay);
  });

  #columns = signal<{ [columnName: string]: ApizUIDataField }>({});

  #data = signal<IupicsData | undefined>(undefined);

  constructor() {
    this.#destroyRef.onDestroy(() => {
      this.#processes.set({});
      this.#columns.set({});
    });
  }

  registerProcess(process: ToolbarProcess): void {
    this.#processes.update((processes) => ({ ...processes, [process.componentUUID]: process }));
  }

  updateProcess(uuid: string, partialProcess: Partial<ToolbarProcess>): void {
    this.#processes.update((processes) => {
      const process = processes[uuid];
      return { ...processes, [uuid]: { ...process, ...partialProcess } };
    });
  }

  registerColumn(columnName: string, data: ApizUIDataField): void {
    this.#columns.update((columns) => ({ ...columns, [columnName]: data }));
  }

  setEditViewData(data: IupicsData): void {
    this.#data.set(data);
  }

  getColumn(columnName: string): ApizUIDataField {
    return this.#columns()[columnName];
  }

  getDataColumn(columnName: string, key: keyof ApizUIDataField): unknown {
    return this.#columns()[columnName][key];
  }

  getDataColumns<K extends keyof ApizUIDataField>(key: K): { [columnName: string]: ApizUIDataField[K] } {
    const response: { [columnName: string]: ApizUIDataField[K] } = {};
    const columns = this.#columns();
    for (const column in columns) {
      response[column] = columns[column][key];
    }
    return response;
  }

  getEditViewData(): IupicsData | undefined {
    return this.#data();
  }
}

export const [injectEditViewService, provideEditViewService] = [
  (): EditViewService => inject(EditViewService),
  (): typeof EditViewService => EditViewService,
];
